import React from 'react';
import styled from 'styled-components';

export interface CheckboxProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    label?: string;
    className?: string;
    id?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({
    checked,
    onChange,
    disabled,
    label,
    className,
    id,
}) => (
    <Root className={className} $disabled={disabled} htmlFor={id}>
        <HiddenInput
            id={id}
            type='checkbox'
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
        />
        <Box $checked={checked} $disabled={disabled} aria-hidden>
            {checked && (
                <svg viewBox='0 0 16 16' fill='none' stroke='currentColor' strokeWidth={2.4} strokeLinecap='round' strokeLinejoin='round'>
                    <path d='m3.5 8.5 3 3 6-6.5' />
                </svg>
            )}
        </Box>
        {label && <LabelText $disabled={disabled}>{label}</LabelText>}
    </Root>
);

export default Checkbox;

const Root = styled.label<{ $disabled?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    user-select: none;
    min-height: 2rem;
`;

const HiddenInput = styled.input`
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
`;

const Box = styled.span<{ $checked: boolean; $disabled?: boolean }>`
    display: inline-grid;
    place-items: center;
    width: 1.375rem;
    height: 1.375rem;
    border-radius: 0.375rem;
    border: 1.5px solid
        ${({ theme, $checked }) => ($checked ? theme.colors.accent : theme.colors.inputBorder)};
    background: ${({ theme, $checked }) => ($checked ? theme.colors.accent : theme.colors.surface)};
    color: ${({ theme }) => theme.colors.ink};
    transition:
        background ${({ theme }) => theme.motion.fast} ease,
        border-color ${({ theme }) => theme.motion.fast} ease,
        box-shadow ${({ theme }) => theme.motion.fast} ease;
    opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }

    ${HiddenInput}:focus-visible + & {
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }

    ${Root}:hover & {
        border-color: ${({ theme, $checked, $disabled }) =>
            $disabled
                ? undefined
                : $checked
                  ? theme.colors.accentDeep
                  : theme.colors.inputBorderHover};
    }
`;

const LabelText = styled.span<{ $disabled?: boolean }>`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme, $disabled }) => ($disabled ? theme.colors.ink4 : theme.colors.ink2)};
    line-height: 1.2;
`;
